import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export interface DocumentPrompt {
  id: string;
  document_type: string;
  prompt_name: string;
  system_prompt: string;
  user_prompt_template: string;
  is_active: boolean;
  is_default: boolean;
  created_by?: string;
  metadata?: Json;
  created_at: string;
  updated_at: string;
}

export interface PromptSelection {
  [document_type: string]: string; // document_type -> prompt_id
}

export class DocumentPromptsService {
  static async getActivePrompts(): Promise<DocumentPrompt[]> {
    const { data, error } = await supabase
      .from('document_prompts')
      .select('*')
      .eq('is_active', true)
      .order('document_type', { ascending: true })
      .order('is_default', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async getPromptsGroupedByType(): Promise<Record<string, DocumentPrompt[]>> {
    const prompts = await this.getActivePrompts();
    return prompts.reduce((acc, prompt) => {
      if (!acc[prompt.document_type]) {
        acc[prompt.document_type] = [];
      }
      acc[prompt.document_type].push(prompt);
      return acc;
    }, {} as Record<string, DocumentPrompt[]>);
  }

  static async getDefaultPrompts(): Promise<Record<string, DocumentPrompt>> {
    const { data, error } = await supabase
      .from('document_prompts')
      .select('*')
      .eq('is_active', true)
      .eq('is_default', true);

    if (error) throw error;
    
    return (data || []).reduce((acc, prompt) => {
      acc[prompt.document_type] = prompt;
      return acc;
    }, {} as Record<string, DocumentPrompt>);
  }

  static async getPromptById(id: string): Promise<DocumentPrompt | null> {
    const { data, error } = await supabase
      .from('document_prompts')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  }

  static async createPrompt(prompt: Omit<DocumentPrompt, 'id' | 'created_at' | 'updated_at'>): Promise<DocumentPrompt> {
    const { data, error } = await supabase
      .from('document_prompts')
      .insert(prompt)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updatePrompt(id: string, updates: Partial<DocumentPrompt>): Promise<DocumentPrompt> {
    const { data, error } = await supabase
      .from('document_prompts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deletePrompt(id: string): Promise<void> {
    const { error } = await supabase
      .from('document_prompts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  static async setDefaultPrompt(documentType: string, promptId: string): Promise<void> {
    // First, unset all defaults for this document type
    await supabase
      .from('document_prompts')
      .update({ is_default: false })
      .eq('document_type', documentType);

    // Then set the new default
    const { error } = await supabase
      .from('document_prompts')
      .update({ is_default: true })
      .eq('id', promptId);

    if (error) throw error;
  }

  static getDocumentTypeDisplayName(documentType: string): string {
    const displayNames: Record<string, string> = {
      'resume': 'Resume',
      'cover-letter': 'Cover Letter',
      'linkedin-profile': 'LinkedIn Profile',
      'thank-you-letter': 'Thank You Letter',
      'outreach-letter': 'Outreach Letter'
    };
    return displayNames[documentType] || documentType;
  }

  static getAvailableDocumentTypes(): string[] {
    return ['resume', 'cover-letter', 'linkedin-profile', 'thank-you-letter', 'outreach-letter'];
  }
}